import type { SettingsCategory } from '../schemas/settings.schema.js';

/**
 * WHICH PERMISSION MODULE GUARDS WHICH SETTINGS CATEGORY.
 *
 *  company    who the company is: name, branding, regional formats, wording,
 *             scheduled reports. The Administrator manages it; HR may read it.
 *  workforce  company policy about employees: attendance rules, the workday,
 *             overtime, leave, letters, UAE compliance and the WPS bank. HR
 *             reads AND edits these, exactly as it could before the split.
 *  app        how the application itself runs: security policy, feature
 *             switches, face recognition, notification channels and the ERP
 *             connection. Platform — Super Administrator only by default.
 *
 * One table read by the API (per-category gate, `/all` filtering) and by the
 * console (which groups and fields to draw). `satisfies` makes a category added
 * to the schema and forgotten here a typecheck error, not a page that leaks.
 */
export const SETTINGS_SCOPES = ['settings.company', 'settings.workforce', 'settings.app'] as const;
export type SettingsScope = (typeof SETTINGS_SCOPES)[number];

export const SETTINGS_CATEGORY_SCOPE = {
  general: 'settings.company',
  branding: 'settings.company',
  terminology: 'settings.company',
  reportSchedules: 'settings.company',
  attendance: 'settings.workforce',
  defaultWorkday: 'settings.workforce',
  overtime: 'settings.workforce',
  leave: 'settings.workforce',
  letters: 'settings.workforce',
  uaeCompliance: 'settings.workforce',
  faceRecognition: 'settings.app',
  security: 'settings.app',
  notifications: 'settings.app',
  features: 'settings.app',
  erp: 'settings.app',
} as const satisfies Record<SettingsCategory, SettingsScope>;

/** Any settings box at all — enough to open Settings; the per-category rule decides the rest. */
export const ANY_SETTINGS_VIEW_KEYS: readonly string[] = SETTINGS_SCOPES.flatMap((s) => [`${s}.view`, `${s}.edit`]);
export const ANY_SETTINGS_EDIT_KEYS: readonly string[] = SETTINGS_SCOPES.map((s) => `${s}.edit`);

export type SettingsAccess = 'view' | 'edit';

/** Whether `held` lets its owner read (or change) one settings category. */
export function mayAccessSettingsCategory(
  held: { has(key: string): boolean },
  category: SettingsCategory,
  access: SettingsAccess
): boolean {
  const scope = SETTINGS_CATEGORY_SCOPE[category];
  // Editing implies reading: a role that can change a category can also see it.
  return access === 'view'
    ? held.has(`${scope}.view`) || held.has(`${scope}.edit`)
    : held.has(`${scope}.edit`);
}

/** Every category the holder may read. */
export function readableSettingsCategories(held: { has(key: string): boolean }): SettingsCategory[] {
  return (Object.keys(SETTINGS_CATEGORY_SCOPE) as SettingsCategory[]).filter((c) =>
    mayAccessSettingsCategory(held, c, 'view')
  );
}

/**
 * The settings blob narrowed to what `held` may read. A key that is not a named
 * category (the app-lock block) is application-side: a key added later and left
 * out of the table is withheld rather than leaked.
 */
export function pickReadableSettings<T extends object>(all: T, held: { has(key: string): boolean }): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(all) as Array<keyof T & string>) {
    const scope = (SETTINGS_CATEGORY_SCOPE as Record<string, SettingsScope>)[key] ?? 'settings.app';
    if (held.has(`${scope}.view`) || held.has(`${scope}.edit`)) out[key] = all[key];
  }
  return out;
}
